import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { fmtINR, fmtNum } from '@/lib/api';
import { Loading, KpiCard, SectionTitle } from '@/components/Shared';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { ShoppingCart, Truck, Fuel, Wallet, Users, Clock, CheckCircle2, AlertCircle, Building2, AlertTriangle } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const COLORS = ['hsl(214 84% 34%)', 'hsl(28 92% 52%)', 'hsl(162 62% 34%)', 'hsl(43 92% 50%)', 'hsl(0 74% 52%)'];
// Static chart prop objects hoisted to avoid re-creating them on every render.
const AXIS_TICK = { fontSize: 12 };
const TOOLTIP_STYLE = { borderRadius: 8, border: '1px solid hsl(var(--border))' };

// Warns when customers exceed their credit limit or invoices pass the 20-day credit cap
const CreditAlertsPanel = () => {
  const nav = useNavigate();
  const [alerts, setAlerts] = useState(null);
  useEffect(() => { api.get('/credit-alerts').then((r) => setAlerts(r.data)).catch(() => {}); }, []);
  if (!alerts || alerts.total_alerts === 0) return null;
  return (
    <div className="rounded-xl border border-destructive/50 bg-destructive/5 p-4" data-testid="credit-alerts-panel">
      <p className="font-semibold flex items-center gap-2 text-destructive">
        <AlertTriangle className="h-4 w-4" />Credit Alerts ({alerts.total_alerts})
      </p>
      <div className="mt-3 grid gap-4 md:grid-cols-2">
        {alerts.over_limit.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Over Credit Limit</p>
            <div className="mt-1.5 space-y-1.5">
              {alerts.over_limit.map((c) => (
                <button key={c.customer_id} type="button" onClick={() => nav(`/admin/customers/${c.customer_id}`)}
                  className="w-full text-left rounded-lg bg-card border border-border px-3 py-2 text-sm hover:border-destructive/60 transition-colors"
                  data-testid={`credit-alert-overlimit-${c.customer_id}`}>
                  <span className="font-semibold">{c.company_name}</span>
                  <span className="block text-xs text-muted-foreground">Outstanding {fmtINR(c.outstanding)} vs limit {fmtINR(c.credit_limit)} — <b className="text-destructive">over by {fmtINR(c.over_by)}</b></span>
                </button>
              ))}
            </div>
          </div>
        )}
        {alerts.overdue_invoices.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Invoices Past {alerts.max_credit_days} Days</p>
            <div className="mt-1.5 space-y-1.5">
              {alerts.overdue_invoices.slice(0, 5).map((inv) => (
                <button key={inv.invoice_id} type="button" onClick={() => nav('/admin/billing')}
                  className="w-full text-left rounded-lg bg-card border border-border px-3 py-2 text-sm hover:border-destructive/60 transition-colors"
                  data-testid={`credit-alert-overdue-${inv.invoice_id}`}>
                  <span className="font-semibold">{inv.invoice_no}</span> · {inv.customer_name}
                  <span className="block text-xs text-muted-foreground">Balance {fmtINR(inv.balance)} — <b className="text-destructive">{inv.days_old} days old</b></span>
                </button>
              ))}
              {alerts.overdue_invoices.length > 5 && <p className="text-xs text-muted-foreground">+{alerts.overdue_invoices.length - 5} more overdue invoices</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [company, setCompany] = useState('all');
  const [companies, setCompanies] = useState([]);
  useEffect(() => { api.get('/billing-companies').then((r) => setCompanies(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setD(null);
    api.get(`/admin/dashboard?company=${company}`).then((r) => setD(r.data));
  }, [company]);
  const companyFilter = (
    <Select value={company} onValueChange={setCompany}>
      <SelectTrigger className="w-[220px]" data-testid="dashboard-company-filter">
        <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
        <SelectValue placeholder="Select company" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Companies</SelectItem>
        {companies.map((co) => <SelectItem key={co.id} value={co.id}>{co.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  if (!d) return (
    <div className="space-y-6">
      <SectionTitle action={companyFilter}>Operations Dashboard</SectionTitle>
      <Loading />
    </div>
  );
  const k = d.kpis;
  return (
    <div className="space-y-6">
      <SectionTitle action={companyFilter}>Operations Dashboard</SectionTitle>
      <CreditAlertsPanel />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Today's Revenue" value={fmtINR(k.today_revenue)} icon={Wallet} testId="kpi-today-revenue" />
        <KpiCard label="Diesel Sold (L)" value={fmtNum(k.diesel_litres_sold)} icon={Fuel} testId="kpi-diesel-litres" />
        <KpiCard label="Orders Today" value={fmtNum(k.today_orders)} icon={ShoppingCart} testId="kpi-orders-today" />
        <KpiCard label="Active Deliveries" value={fmtNum(k.active_deliveries)} icon={Truck} testId="kpi-active-deliveries" />
        <KpiCard label="Pending Orders" value={fmtNum(k.pending_orders)} icon={Clock} testId="kpi-pending" />
        <KpiCard label="Outstanding" value={fmtINR(k.outstanding)} icon={AlertCircle} testId="kpi-outstanding" />
        <KpiCard label="Active Drivers" value={fmtNum(k.active_drivers)} icon={Truck} />
        <KpiCard label="Customers" value={fmtNum(k.total_customers)} icon={Users} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-4">
          <p className="font-semibold mb-3">Revenue & Litres (Last 7 days)</p>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={d.revenue_trend}>
              <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={COLORS[0]} stopOpacity={0.3} /><stop offset="95%" stopColor={COLORS[0]} stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
              <XAxis dataKey="date" tick={AXIS_TICK} /><YAxis tick={AXIS_TICK} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => fmtINR(v)} />
              <Area type="monotone" dataKey="revenue" stroke={COLORS[0]} fill="url(#rev)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="font-semibold mb-3">Payment Status</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart><Pie data={d.payment_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80}>
              {d.payment_distribution.map((e, i) => <Cell key={e.name} fill={COLORS[i % COLORS.length]} />)}
            </Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 justify-center">{d.payment_distribution.map((e, i) => <span key={e.name} className="text-xs flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{e.name} ({e.value})</span>)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="font-semibold mb-3">Orders (Last 7 days)</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={d.revenue_trend}><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} /><XAxis dataKey="date" tick={AXIS_TICK} /><YAxis tick={AXIS_TICK} /><Tooltip /><Bar dataKey="orders" fill={COLORS[2]} radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="font-semibold mb-3">Top Customers</p>
          <div className="space-y-2">
            {d.top_customers.map((c) => (
              <div key={c.name} className="flex items-center justify-between"><span className="text-sm">{c.name}</span><span className="font-semibold text-sm">{fmtINR(c.revenue)}</span></div>
            ))}
            {d.top_customers.length === 0 && <p className="text-sm text-muted-foreground">No data yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
